import React, { useState, useRef, useEffect, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import makeStyles from '@mui/styles/makeStyles';
import Switch from '@mui/material/Switch';
import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import { grey } from '@mui/material/colors';
import qs from 'qs';
import lowerCase from 'lodash/lowerCase';
import isNil from 'lodash/isNil';

import { Datatable, InfoTooltip, ScoreChangeIndicator } from 'ui';
import { CreateNewPortfolioModal } from 'modules/common/Modals';
import { RequestConfirmationModal } from 'modules/creditReview/CreditReviewModals';
import { LinkButton, DownloadButton } from 'ui/Buttons';
import { RequestButton, SearchFiltersModal } from 'modules/common';

import { requestSigEntityRatingAction } from 'store/entity/entityActions';
import { setSearchFiltersAction, searchStartAction } from 'store/searchResult/searchResultActions';
import { API_URL } from 'utils';
import { exportService } from 'services/export';
import { useTable, usePortfolioModals, useNotifications } from 'shared/hooks';
import {
  formatDate,
  removeItemsFromPortfolio,
  putNotifyError,
  performInNextCycle,
} from 'shared/functions';
import { cofaceAccessTypes } from 'shared/constants/creditReview/cofaceAccessTypes';
import { creditReviewRequestTooltips } from 'shared/constants/creditReview/creditReviewRequestTooltips';
import { orderTypes } from 'shared/constants/orderTypes';
import { ratingTypes } from 'shared/constants/ratingTypes';
import { toggleMonitoring } from 'services';
import { creditReviewLevelTypes } from 'shared/constants/creditReview/creditReviewLevelTypes';
import { tableRefreshReasonTypes as refreshReasons } from 'shared/constants/tableRefreshReasonTypes';
import { CreditReviewLevelsPopover } from 'modules/creditReview';
import { useConfirmedRequest } from 'shared/hooks/useConfirmedRequest/useConfirmedRequest';
import { getEntityShouldRefresh } from 'selectors/entity';
import { dedicatedPortfoliosLabels } from 'shared/constants/creditReview/dedicatedPortfoliosLabels';
import { activityMappingCreditReview } from 'shared/mappings/activityLabels';
import { exportModes } from 'shared/constants/exportModes';

import { addNotificationAction } from 'store/notifications/notificationsActions';
import { UpdateEntitiesModal } from 'modules/common/Modals/UpdateEntitiesModal/UpdateEntitiesModal';
import { ItemType } from 'shared/types/portfolios';
import CreditReviewEntitiesFilterModal from './CreditReviewEntitiesFilterModal';

const useStyles = makeStyles((theme) => ({
  row: {
    position: 'relative',
  },
  requestCell: {
    backgroundColor: grey[100],
  },
  requestButton: {
    textTransform: 'none',
    fontWeight: 400,
    color: theme.palette.text.primary,
    borderColor: theme.palette.text.primary,
    borderRadius: 16,
    height: 32,
    transform: 'translate(-50%)',
  },
  pendingText: {
    transform: ({ isRequestHistory }) => `translate(${isRequestHistory ? -80 : -62}%)`,
    whiteSpace: 'nowrap',
    cursor: 'default',
  },
  refineSearchLink: {
    marginLeft: theme.spacing(1),
    textDecoration: 'underline',
    cursor: 'pointer',
  },
  tooltipIcon: {
    marginLeft: theme.spacing(1),
  },
  monitoringIconEnabled: {
    fill: theme.palette.primary.main,
  },
  monitoringIconDisabled: {
    fill: grey[300],
  },
  monitoringIcon: {
    cursor: 'pointer',
  },
}));

const setCellProps = (classes) => (value) => {
  // Here we're fetching the flag from DataAvailabilityCell alreadyRequested component property
  const wasRequested = value.props && value.props.alreadyRequested;

  return wasRequested === false // that needs to be explicit comparison to boolean (false)
    ? {
        className: classes.requestCell,
      }
    : null;
};

/* 
  This needs to be done this way because in setCellProps function we don't have an access to other column values (wasRequested in this case)
  That alreadyRequested property is needed in setCellProps to get the value of that flag which is crucial for showing the ratings
*/
// eslint-disable-next-line no-unused-vars
const DataAvailabilityCell = ({ alreadyRequested, children }) => {
  return children;
};

DataAvailabilityCell.defaultProps = {
  alreadyRequested: false,
  children: null,
};

DataAvailabilityCell.propTypes = {
  alreadyRequested: PropTypes.bool,
  children: PropTypes.node,
};

const CreditReviewEntities = ({
  paginationLimit,
  dataSelector,
  actionCreator,
  actionCreatorParams,
  constantFilter,
  initialFilter,
  initialFilterModalValues,
  filterableIndustries,
  searchQuery,
  dossierId,
  cofaceAccessType,
  extendedExport,
}) => {
  const { error, isPending, data, total } = dataSelector || {};
  const isPortfolioTable = !!dossierId;
  const { singleDossier: dossierData } = useSelector(({ dossier }) => dossier);
  const entityShouldRefresh = useSelector(getEntityShouldRefresh);
  const isDedicatedPortfolio = isPortfolioTable && dossierData && dossierData.isDedicated;
  const isSamplePortfolio =
    isDedicatedPortfolio &&
    lowerCase(dedicatedPortfoliosLabels.samplePortfolio) === lowerCase(dossierData.name);
  const isRequestHistory =
    isDedicatedPortfolio &&
    lowerCase(dedicatedPortfoliosLabels.requestHistory) === lowerCase(dossierData.name);

  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { notifyError } = useNotifications();
  const didUnmount = useRef(false);
  const classes = useStyles({ isRequestHistory });
  const isRequestAvailable = cofaceAccessType === cofaceAccessTypes.REQUEST_ALLOWED;

  const requestButtonTooltipTitle = creditReviewRequestTooltips[cofaceAccessType];

  const ratingType = ratingTypes.CREDIT_REVIEW;

  const [selected, setSelected] = useState({ items: {}, count: 0 });

  const { createPortfolio, addToPortfolio } = usePortfolioModals();

  const [dossiersRequest, setDossiersRequest] = useState({ items: {}, count: 0, type: null });

  const [shouldRefresh, setShouldRefresh] = useState({ value: false, reason: null });

  const [pendingMonitoringChange, setPendingMonitoringChange] = useState([]);

  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [searchFiltersModalOpen, setSearchFiltersModalOpen] = useState(false);
  const [requestConfirmationModalOpen, setRequestConfirmationModalOpen] = useState(false);

  const { data: creditReviewLevels } = useSelector(({ shared }) => shared.creditReviewLevels);

  const { userId, organisationId } = useSelector(({ auth }) => auth.data);

  const [requestLoading, setRequestLoading] = useState(false);

  const [showSearchFilters, setShowSearchFilters] = useState(false);

  const [showSelectionExtension, setShowSelectionExtension] = useState(false);
  const [selectedAll, setSelectedAll] = useState(false);

  const requestInProgressTooltip = t('ENTITY.THIS_SCORE_IS_NOT_YET_AVAILABLE');

  const toggleRequestConfirmationModal = () =>
    setRequestConfirmationModalOpen(!requestConfirmationModalOpen);
  const doRequestConfirmationModalOpen = () => setRequestConfirmationModalOpen(true);

  const setRowProps = () => ({
    className: classes.row,
  });

  const initialSort = isRequestHistory
    ? {
        sortColumn: 'requestDate',
        direction: 'DESC',
      }
    : {
        sortColumn: 'activity',
        direction: 'ASC',
      };

  const handleToggleMonitoring = ({ itemId }) => {
    setRequestLoading(true);
    const itemsIds = itemId ? [itemId] : Object.values(selected.itemIds);

    toggleMonitoring({
      itemsIds,
      selectAll: selectedAll,
      sourceDossierId: dossierId,
    })
      .then(() => {
        if (!didUnmount.current) {
          setPendingMonitoringChange(
            selectedAll && data && data.length ? data.map((item) => item[0]) : [...itemsIds],
          );
          dispatch(
            addNotificationAction({
              message: t('CREDIT_REVIEW.MONITORING_CHANGE_SUCCESS'),
              variant: 'success',
            }),
          );
        }
      })
      .catch(() => {
        notifyError(t('Failed to change monitoring state'));
      })
      .finally(() => {
        if (!didUnmount.current) {
          setRequestLoading(false);
        }
      });
  };

  const handleRequest = () => {
    dispatch(
      requestSigEntityRatingAction({
        ids: Object.values(selected.itemIds),
        organisationId,
        userId,
        selectAll: selectedAll,
      }),
    );
  };

  const { scheduleRequest } = useConfirmedRequest({
    handleRequest,
    confirmationModalOpen: doRequestConfirmationModalOpen,
  });

  const handleRequestSelected = () => {
    scheduleRequest();
  };

  const handleRemove = () => {
    setRequestLoading(true);

    removeItemsFromPortfolio({
      dossierId,
      dossierItemsIds: Object.values(selected.itemIds),
      ratingType,
      selectAll: selectedAll,
    })
      .then(() => {
        if (!didUnmount.current) {
          setShouldRefresh({ value: true, reason: refreshReasons.DELETED });
        }
      })
      .finally(() => {
        if (!didUnmount.current) {
          setRequestLoading(false);
        }
      });
  };

  const monitoredRenderer = useCallback(
    (_value, { rowData }) => {
      const monitored = rowData[13];
      const easyNumber = rowData[0];
      const pending = pendingMonitoringChange.includes(easyNumber);
      const tooltip = pending ? t('CREDIT_REVIEW.MONITORING_PENDING') : '';
      return (
        <Tooltip title={tooltip} placement="top">
          <Box display="flex" justifyContent="center">
            <Switch
              checked={monitored}
              onChange={() => handleToggleMonitoring({ itemId: easyNumber })}
              color="primary"
              disabled={pending}
            />
          </Box>
        </Tooltip>
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pendingMonitoringChange],
  );

  const companyNameRenderer = useCallback(
    ([entityName, companyVat, companyHrn, companyLei, requested], { rowData: [easyNumber] }) => {
      const tooltip = [
        companyVat && `VAT: ${companyVat}`,
        companyHrn && `HRN: ${companyHrn}`,
        companyLei && `LEI: ${companyLei}`,
      ]
        .filter((text) => text)
        .join(' ');

      const detailedViewRestricted =
        !isSamplePortfolio && !isRequestAvailable && !(requested && !isNil(requested.score));

      return detailedViewRestricted ? (
        <Tooltip title={tooltip} placement="top">
          <Typography variant="button">{entityName.toUpperCase()}</Typography>
        </Tooltip>
      ) : (
        <LinkButton
          to={`entities/${easyNumber}/details?companyModule=${ratingType}`}
          tooltip={tooltip}
          label={entityName.toUpperCase()}
          withPathname
        />
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const companyCityRenderer = useCallback(([city, address]) => {
    const tooltip = address && `Address: ${address}`;
    return (
      <Box display="flex" alignItems="center">
        <Typography>{city}</Typography>
        {tooltip ? <InfoTooltip title={tooltip} iconClassName={classes.tooltipIcon} /> : null}
      </Box>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const creditScoreRenderer = useCallback((_, { rowData }) => {
    const scoreRequest = rowData[12];
    const isDataRequested = Boolean(scoreRequest && !isNil(scoreRequest.score));

    return isDataRequested ? (
      <Chip label={scoreRequest.score} variant="outlined" color="primary" />
    ) : (
      <DataAvailabilityCell alreadyRequested={isDataRequested} />
    );
  }, []);

  const probabilityOfDefaultRenderer = useCallback((_, { rowData }) => {
    const scoreRequest = rowData[12];
    const isDataRequested = Boolean(scoreRequest && !isNil(scoreRequest.score));
    const probabilityOfDefaultExists =
      scoreRequest.probability_of_default_definition &&
      scoreRequest.probability_of_default_definition !== '-';

    const probabilityOfDefaultValue = scoreRequest.probability_of_default || 0;

    return isDataRequested ? (
      <Tooltip
        placement="top"
        title={probabilityOfDefaultExists ? scoreRequest.probability_of_default_definition : ''}
      >
        {probabilityOfDefaultExists ? (
          <Chip label={`${probabilityOfDefaultValue}%`} variant="outlined" />
        ) : (
          <Typography>-</Typography>
        )}
      </Tooltip>
    ) : (
      <DataAvailabilityCell alreadyRequested={isDataRequested} />
    );
  }, []);

  const sectorRiskLevelRenderer = useCallback((value, { rowData }) => {
    const easyNumber = rowData[0];
    const scoreRequest = rowData[12];
    const isDataRequested = Boolean(scoreRequest && !isNil(scoreRequest.score));

    return isDataRequested ? (
      <CreditReviewLevelsPopover
        activeLevel={value}
        levelType={creditReviewLevelTypes.sectorRiskLevel}
      />
    ) : (
      <RequestButton
        className={classes.requestButton}
        tooltip={t(requestButtonTooltipTitle)}
        disabled={!isRequestAvailable}
        id={easyNumber}
        orderType={orderTypes.SIG_ENTITY}
        alreadyRequested={isDataRequested}
        product={{ requested: isDataRequested, userId, organisationId }}
        ratingType={ratingType}
        confirmationModalOpen={doRequestConfirmationModalOpen}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const businessClimateRenderer = useCallback((value, { rowData }) => {
    const easyNumber = rowData[0];
    const scoreRequest = rowData[12];
    const isDataRequested = Boolean(scoreRequest && !isNil(scoreRequest.score));

    return isDataRequested ? (
      <CreditReviewLevelsPopover
        activeLevel={value}
        levelType={creditReviewLevelTypes.businessClimate}
      />
    ) : (
      <RequestButton
        className={classes.requestButton}
        tooltip={t(requestButtonTooltipTitle)}
        disabled={!isRequestAvailable}
        id={easyNumber}
        orderType={orderTypes.SIG_ENTITY}
        alreadyRequested={isDataRequested}
        product={{ requested: isDataRequested, userId, organisationId }}
        ratingType={ratingType}
        confirmationModalOpen={doRequestConfirmationModalOpen}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const countryRiskAssessmentRenderer = useCallback((_value, { rowData }) => {
    const easyNumber = rowData[0];
    const scoreRequest = rowData[12];

    const isDataRequested = Boolean(scoreRequest && !isNil(scoreRequest.score));

    if (!scoreRequest) {
      return (
        <RequestButton
          className={classes.requestButton}
          tooltip={t(requestButtonTooltipTitle)}
          disabled={!isRequestAvailable}
          id={easyNumber}
          orderType={orderTypes.SIG_ENTITY}
          alreadyRequested={isDataRequested}
          product={{ requested: isDataRequested, userId, organisationId }}
          ratingType={ratingType}
          confirmationModalOpen={doRequestConfirmationModalOpen}
        />
      );
    }

    if (isNil(scoreRequest.score)) {
      return (
        <DataAvailabilityCell alreadyRequested={false}>
          <Tooltip title={requestInProgressTooltip} placement="top">
            <Box className={classes.pendingText}>{t('Request in progress')}</Box>
          </Tooltip>
        </DataAvailabilityCell>
      );
    }

    return (
      <CreditReviewLevelsPopover
        activeLevel={scoreRequest.country_risk_assessment}
        levelType={creditReviewLevelTypes.countryRiskAssessment}
      />
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const purchaseRenderer = useCallback((_value, { rowData }) => {
    const easyNumber = rowData[0];

    return (
      <DownloadButton
        disabled={!isRequestAvailable}
        url={`${API_URL}/sig/generate-report/${easyNumber}`}
      >
        {t('Purchase')}
      </DownloadButton>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scoreDateRenderer = useCallback((_value, { rowData }) => {
    const scoreDate = rowData[11];

    return scoreDate ? formatDate(scoreDate, 'dd/MM/yyyy') : '-';
  }, []);

  const activityRenderer = useCallback((activity, { rowData }) => {
    const scoreRequest = rowData[12];
    return activity && scoreRequest ? (
      <ScoreChangeIndicator
        activity={activity}
        activityMapping={activityMappingCreditReview}
        scoreChangeDate={scoreRequest.score_date}
        scoreOld={scoreRequest.old_score}
        showLabel
      />
    ) : null;
  }, []);

  const buildInitialColumns = useCallback(
    () => [
      {
        name: 'easyNumber',
        label: t('Easy Number'),
        options: { sort: false, display: false, viewColumns: false },
      },
      {
        name: 'companyName',
        label: t('ENTITY.NAME'),
        options: {
          sort: true,
          customBodyRender: companyNameRenderer,
        },
      },
      { name: 'companyCountryIso', label: t('Country'), options: { sort: true } },
      { name: 'industry', label: t('Industry'), options: { sort: true, display: false } },
      {
        name: 'city',
        label: t('City'),
        options: {
          sort: true,
          customBodyRender: companyCityRenderer,
        },
      },
      { name: 'companyPostcode', label: t('Postcode'), options: { sort: true } },
      {
        name: 'creditScore',
        label: t('PORTFOLIO.CREDIT_REVIEW'),
        options: {
          sort: true,
          setCellProps: setCellProps(classes),
          customBodyRender: creditScoreRenderer,
        },
      },
      {
        name: 'probabilityOfDefault',
        label: t('Probability of Default'),
        options: {
          sort: true,
          setCellProps: setCellProps(classes),
          customBodyRender: probabilityOfDefaultRenderer,
        },
      },
      {
        name: 'sectorRiskLevel',
        label: t('Industry Risk Level'),
        options: {
          sort: true,
          display: false,
          setCellProps: setCellProps(classes),
          customBodyRender: sectorRiskLevelRenderer,
        },
      },
      {
        name: 'businessClimate',
        label: t('Business Climate'),
        options: {
          sort: true,
          display: false,
          setCellProps: setCellProps(classes),
          customBodyRender: businessClimateRenderer,
        },
      },
      {
        name: 'countryRiskAssessment',
        label: t('Country Risk Assessment'),
        options: {
          sort: true,
          setCellProps: setCellProps(classes),
          customBodyRender: countryRiskAssessmentRenderer,
        },
      },
      {
        name: 'purchase',
        label: 'Purchase',
        options: {
          sort: false,
          display: false,
          setCellProps: setCellProps(classes),
          customBodyRender: purchaseRenderer,
        },
      },
      {
        name: 'monitoring',
        label: 'Keep in subscription',
        options: {
          display: isRequestHistory,
          sort: true,
          customBodyRender: monitoredRenderer,
        },
      },
      {
        name: 'scoreDate',
        label: t('ENTITY.LATEST_UPDATE'),
        options: {
          display: isPortfolioTable,
          sort: true,
          sortDirection: isRequestHistory ? 'asc' : undefined,
          customBodyRender: scoreDateRenderer,
        },
      },
      {
        name: 'activity',
        label: t('ENTITY.ACTIVITY'),
        options: {
          display: isPortfolioTable,
          sort: true,
          sortDirection: isRequestHistory ? undefined : 'asc',
          hint: t('ENTITY.ACTIVITY_HINT'),
          customBodyRender: activityRenderer,
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [monitoredRenderer],
  );

  const [initialColumns, setInitialColumns] = useState(buildInitialColumns());

  useEffect(() => {
    setInitialColumns(buildInitialColumns());
  }, [pendingMonitoringChange, buildInitialColumns]);

  const { columns, pagination, onTableChange, onSortChange, isFiltered, setFilter, refreshTable } =
    useTable({
      paginationLimit,
      initialColumns,
      getTableDataAction: actionCreator,
      getTableDataActionParams: actionCreatorParams,
      constantFilter,
      initialFilter,
      initialSort,
    });

  useEffect(() => {
    if ((shouldRefresh.value || entityShouldRefresh) && refreshTable) {
      dispatch(refreshTable);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldRefresh, entityShouldRefresh]);

  const toggleFiltersModal = () => setFiltersModalOpen(!filtersModalOpen);
  const toggleSearchFiltersModal = () => setSearchFiltersModalOpen(!searchFiltersModalOpen);

  const onSelectedChange = (selectedItems) => {
    setSelected({ ...selectedItems });
    setDossiersRequest({ ...selectedItems, type: ItemType.EASYNUMBER });

    if (data && selectedItems.count >= data.length && selectedItems.count < total) {
      setShowSelectionExtension(true);
    } else {
      setShowSelectionExtension(false);
      setSelectedAll(false);
    }
  };

  useEffect(() => {
    setRequestLoading(isPending);
  }, [isPending]);

  useEffect(() => {
    if (error && ![undefined].includes(error.status)) {
      setSearchFiltersModalOpen(true);
    }
  }, [error]);

  const handleFiltersModalSubmit = (values) => {
    const {
      companyName,
      countries,
      sectors,
      creditScoreFrom,
      creditScoreTo,
      sectorRiskLevelFrom,
      sectorRiskLevelTo,
      businessClimateFrom,
      businessClimateTo,
      countryRiskAssessmentFrom,
      countryRiskAssessmentTo,
    } = values;

    const newFilters = {
      ...(companyName && { companyName }),
      ...(countries.length && { countries }),
      ...(sectors.length !== 0 && { sector: sectors }),
      ...(creditScoreFrom !== '1' && { creditScoreFrom }),
      ...(creditScoreTo !== '11' && { creditScoreTo }),
      ...(countryRiskAssessmentFrom !== 'D' && { countryRiskAssessmentFrom }),
      ...(countryRiskAssessmentTo !== 'A1' && { countryRiskAssessmentTo }),
      ...(businessClimateFrom !== 'D' && { businessClimateFrom }),
      ...(businessClimateTo !== 'A1' && { businessClimateTo }),
      ...(sectorRiskLevelFrom !== 'very%20high' && { sectorRiskLevelFrom }),
      ...(sectorRiskLevelTo !== 'low' && { sectorRiskLevelTo }),
    };

    setFilter(newFilters);
    setFiltersModalOpen(false);
  };

  const handleSearchFiltersModalSubmit = (values) => {
    if (!showSearchFilters) {
      setShowSearchFilters(true);
    }
    setSearchFiltersModalOpen(false);

    const { name: query, country, ...filters } = values;

    const queryString = qs.stringify(
      { query, country: [country], ratingType, ...filters },
      { addQueryPrefix: true },
    );

    if (location.search !== queryString) {
      dispatch(searchStartAction);
      dispatch(setSearchFiltersAction({ ...filters, ratingType }));

      /* Peform navigate in next event loop cycle of JS engine.
       * Otherwise the browser is occupied with context switching,
       * hence actions dispatching and processing is significantly delayed. */
      performInNextCycle(() => navigate({ pathname: '/search-result', search: queryString }));
    }
  };

  const downloadErrorHandler = () => {
    putNotifyError(t('ENTITY.FAILED_TO_EXPORT_ENTITIES'));
  };

  const exportToExcel = (items) => {
    exportService({
      items: Object.values(items),
      type: orderTypes.SIG_ENTITY,
      onError: downloadErrorHandler,
      ratingType,
      dossierId,
      selectAll: selectedAll,
      mode: extendedExport ? exportModes.EXTENDED : exportModes.BASIC,
    });
  };

  const toolbarData = [
    {
      text: t(isPortfolioTable ? 'TOOLBAR.COPY_TO_PORTFOLIO' : 'TOOLBAR.ADD_TO_PORTFOLIO'),
      handler: addToPortfolio.show,
      hidden: !selected.count,
    },
    {
      text: t('TOOLBAR.REQUEST_SELECTED'),
      handler: handleRequestSelected,
      disabled: !selected.count,
      hidden: !selected.count,
      data: selected.itemIds,
    },
    {
      text: t('TOOLBAR.EXPORT_TO_EXCEL'),
      handler: exportToExcel,
      data: selected.itemIds,
      hidden: !selected.count,
    },
    {
      text: t('TOOLBAR.TOGGLE_MONITORING'),
      handler: handleToggleMonitoring,
      disabled: !selected.count,
      hidden: !selected.count || !isRequestHistory,
      data: selected.itemIds,
    },
    {
      text: t('TOOLBAR.DELETE'),
      handler: handleRemove,
      disabled: !selected.count || isDedicatedPortfolio,
      hidden: !selected.count || !isPortfolioTable,
      data: selected.itemIds,
    },
  ];

  const selectAll = () => {
    setSelectedAll(true);
  };

  const title = !isPortfolioTable ? (
    <Box display="flex">
      <Typography variant="body1">{t('SEARCH.HAVENT_FOUND_WHAT_YOU_ARE_LOOKING_FOR')} </Typography>
      <Typography
        className={classes.refineSearchLink}
        onClick={() => setSearchFiltersModalOpen(true)}
      >
        {t('SEARCH.REFINE_YOUR_SEARCH')}
      </Typography>
    </Box>
  ) : null;

  return (
    <>
      <Datatable
        title={title}
        loading={requestLoading}
        columns={columns}
        pagination={pagination}
        data={data}
        total={total}
        onSelectedChange={onSelectedChange}
        onTableChange={onTableChange}
        onSortChange={onSortChange}
        toolbarData={toolbarData}
        isFiltered={isFiltered}
        shouldRefresh={shouldRefresh}
        showFilters={isPortfolioTable}
        onApplySearchFiltersClick={() => setSearchFiltersModalOpen(true)}
        onApplyFiltersClick={isPortfolioTable ? () => setFiltersModalOpen(true) : undefined}
        setRowProps={setRowProps}
        showSearchFilters={showSearchFilters}
        removeShadow
        showSelectionExtension={isPortfolioTable ? showSelectionExtension : false}
        onSelectAllClick={isPortfolioTable ? selectAll : undefined}
        selectedAll={isPortfolioTable ? selectedAll : false}
      />
      <SearchFiltersModal
        open={searchFiltersModalOpen}
        onClose={toggleSearchFiltersModal}
        onSubmit={handleSearchFiltersModalSubmit}
        extraMessage={error && error.message}
        searchQuery={searchQuery}
        filterableIndustries={filterableIndustries}
      />
      {creditReviewLevels && (
        <CreditReviewEntitiesFilterModal
          open={filtersModalOpen}
          onClose={toggleFiltersModal}
          onSubmit={handleFiltersModalSubmit}
          searchQuery={searchQuery}
          initialFilter={initialFilterModalValues}
          filterableIndustries={filterableIndustries}
          creditReviewLevels={creditReviewLevels}
        />
      )}
      <UpdateEntitiesModal
        open={addToPortfolio.isOpen}
        onClose={addToPortfolio.close}
        onClickCreateNew={createPortfolio.show}
        itemsData={dossiersRequest}
        ratingType={ratingType}
        selectAll={selectedAll}
        sourcePortfolioId={dossierId}
        itemType="ENTITY"
      />
      <CreateNewPortfolioModal
        open={createPortfolio.isOpen}
        onClose={createPortfolio.close}
        itemsData={dossiersRequest}
        ratingType={ratingType}
        selectAll={selectedAll}
        sourcePortfolioId={dossierId}
        itemType="ENTITY"
      />
      <RequestConfirmationModal
        open={requestConfirmationModalOpen}
        onClose={toggleRequestConfirmationModal}
        requestsCount={selected.count || 1}
      />
    </>
  );
};

CreditReviewEntities.defaultProps = {
  actionCreatorParams: {},
  constantFilter: {},
  initialFilter: {},
  initialFilterModalValues: {},
  filterableIndustries: null,
  searchQuery: null,
  dossierId: null,
  cofaceAccessType: null,
  paginationLimit: null,
  extendedExport: false,
};

CreditReviewEntities.propTypes = {
  dataSelector: PropTypes.shape({
    isPending: PropTypes.bool.isRequired,
    total: PropTypes.number.isRequired,
    data: PropTypes.arrayOf(PropTypes.any).isRequired,
    status: PropTypes.number,
  }).isRequired,
  actionCreator: PropTypes.func.isRequired,
  actionCreatorParams: PropTypes.object,
  constantFilter: PropTypes.object,
  initialFilter: PropTypes.object,
  initialFilterModalValues: PropTypes.object,
  filterableIndustries: PropTypes.arrayOf(PropTypes.object),
  searchQuery: PropTypes.shape({
    query: PropTypes.string,
    country: PropTypes.arrayOf(PropTypes.string),
    industry: PropTypes.arrayOf(PropTypes.string),
    ratingType: PropTypes.string,
  }),
  dossierId: PropTypes.string,
  cofaceAccessType: PropTypes.string,
  paginationLimit: PropTypes.number,
  extendedExport: PropTypes.bool,
};

export default CreditReviewEntities;
